import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import AmountField from '../../../components/AmountField';

const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

// Helper to calculate RD maturity value (quarterly compounding, same as FinanceCalculator.js)
function calculateRDMaturity(monthly, rate, months) {
  const p = parseFloat(monthly);
  const r = parseFloat(rate); // Annual rate in percent
  const n = parseFloat(months); // Total time in months
  if (!p || !r || !n) return 0;
  const quarterlyRate = r / 400;
  const quarters = n / 3;
  const base = 1 + quarterlyRate;
  const numerator = Math.pow(base, quarters) - 1;
  const denominator = 1 - Math.pow(base, -1/3);
  return p * (numerator / denominator);
}

// Helper to calculate maturity date
function getMaturityDate(startDate, tenureMonths) {
  if (!startDate || !tenureMonths) return '-';
  const start = new Date(startDate);
  start.setMonth(start.getMonth() + Number(tenureMonths));
  return start.toLocaleDateString('en-IN');
}

// Helper to calculate total contribution till date
function calculateContribution(startDate, monthlyAmount) {
  if (!startDate || !monthlyAmount) return 0;
  const start = new Date(startDate);
  const today = new Date();
  
  // Calculate months difference
  const monthsDiff = (today.getFullYear() - start.getFullYear()) * 12 + 
                    (today.getMonth() - start.getMonth());
  
  // If start date is in future, return 0
  if (monthsDiff < 0) return 0;
  
  // If start date day is greater than today's date, subtract one month
  // as the current month's installment wouldn't be counted
  const adjustedMonths = start.getDate() > today.getDate() ? monthsDiff : monthsDiff + 1;
  
  return monthlyAmount * Math.max(0, adjustedMonths);
}

// Helper to calculate installment progress
function calculateInstallmentProgress(startDate, totalInstallments) {
  if (!startDate || !totalInstallments) return { paid: 0, total: totalInstallments || 0 };
  const start = new Date(startDate);
  const today = new Date();
  
  // Calculate months difference
  const monthsDiff = (today.getFullYear() - start.getFullYear()) * 12 + 
                    (today.getMonth() - start.getMonth());
  
  // If start date is in future, return 0 paid
  if (monthsDiff < 0) return { paid: 0, total: totalInstallments };
  
  // If start date day is greater than today's date, subtract one month
  const adjustedMonths = start.getDate() > today.getDate() ? monthsDiff : monthsDiff + 1;
  
  // Ensure we don't exceed total installments
  const paidInstallments = Math.min(Math.max(0, adjustedMonths), totalInstallments);
  
  return { paid: paidInstallments, total: totalInstallments };
}

const RecurringDeposit = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-debt-recurring-deposit', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  // Edit form state
  const [editMonthly, setEditMonthly] = useState('');
  const [editInterestRate, setEditInterestRate] = useState('');
  const [editTenureMonths, setEditTenureMonths] = useState('');
  const [editStartDate, setEditStartDate] = useState(new Date());
  const [editNotes, setEditNotes] = useState('');
  // Form state
  const [accountName, setAccountName] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [monthlyInvestment, setMonthlyInvestment] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [tenureYears, setTenureYears] = useState('');
  const [tenureMonths, setTenureMonths] = useState('');
  const [notes, setNotes] = useState('');
  const [userAccountNames, setUserAccountNames] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Fetch investments
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recurring_deposit_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false });
      if (error) throw error;
      if (data && data.length > 0) {
        const uniqueAccountNames = [...new Set(data.map(item => item.name))];
        setUserAccountNames(uniqueAccountNames);
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: calculateRDMaturity(item.monthly_investment, item.interest_rate, item.tenure_months),
          monthly: item.monthly_investment,
          interestRate: item.interest_rate,
          tenureMonths: item.tenure_months,
          startDate: item.start_date,
          notes: item.notes
        }));
        setInvestments(formattedData);
        setTotalValue(formattedData.reduce((sum, acc) => sum + acc.value, 0));
      } else {
        setInvestments([]);
        setTotalValue(0);
        setUserAccountNames([]);
      }
    } catch (error) {
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  // Effect to update contribution and progress values periodically
  useEffect(() => {
    // Update every minute
    const intervalId = setInterval(() => {
      // Force a re-render by updating the investments array with new calculations
      setInvestments(currentInvestments => 
        currentInvestments.map(investment => ({
          ...investment,
          // Adding a timestamp to force recalculation
          _lastUpdate: new Date().getTime()
        }))
      );
    }, 60000); // 60000 ms = 1 minute

    return () => clearInterval(intervalId);
  }, []);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: Math.round(item.value),
    label: item.name,
  }));

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const handleReset = () => {
    setAccountName('');
    setStartDate(new Date());
    setMonthlyInvestment('');
    setInterestRate('');
    setTenureYears('');
    setTenureMonths('');
    setNotes('');
  };
  // Transaction history modal handlers
  const handleOpenTransactionHistory = (account) => {
    setSelectedAccount(account);
    setAccountTransactions([account]); // Show the selected RD as the only transaction
    setOpenHistoryModal(true);
  };
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedAccount(null);
    setAccountTransactions([]);
  };
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    if (!currentTransaction) return;
    setEditMonthly(currentTransaction.monthly ? currentTransaction.monthly.toString() : '');
    setEditInterestRate(currentTransaction.interestRate ? currentTransaction.interestRate.toString() : '');
    setEditTenureMonths(currentTransaction.tenureMonths ? currentTransaction.tenureMonths.toString() : '');
    setEditStartDate(currentTransaction.startDate ? new Date(currentTransaction.startDate) : new Date());
    setEditNotes(currentTransaction.notes || '');
    setOpenEditModal(true);
  };
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  // Save new investment
  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    if (!accountName || !startDate || !monthlyInvestment || !interestRate || (!tenureYears && !tenureMonths)) {
      alert('Please fill all required fields');
      return;
    }
    // Prevent duplicate account names (case-insensitive)
    if (investments.some(inv => inv.name.trim().toLowerCase() === accountName.trim().toLowerCase())) {
      alert('An RD with this account name already exists. Please use a unique name.');
      return;
    }
    // Convert years and months to total months
    const years = parseInt(tenureYears) || 0;
    const months = parseInt(tenureMonths) || 0;
    const totalMonths = years * 12 + months;
    if (totalMonths <= 0) {
      alert('Tenure must be at least 1 month.');
      return;
    }
    try {
      setLoading(true);
      let accountNameValue = '';
      if (typeof accountName === 'string') {
        accountNameValue = accountName;
      } else if (accountName && typeof accountName === 'object') {
        accountNameValue = accountName.label || '';
      }
      if (!accountNameValue) {
        alert('Please enter a valid account name');
        setLoading(false);
        return;
      }
      const newInvestment = {
        name: accountNameValue,
        start_date: startDate.toISOString().split('T')[0],
        monthly_investment: parseFloat(monthlyInvestment),
        interest_rate: parseFloat(interestRate),
        tenure_months: totalMonths,
        notes: notes || null,
        user_id: user.id
      };
      const { data, error } = await supabase
        .from('recurring_deposit_investments')
        .insert([newInvestment])
        .select();
      if (error) throw error;
      if (data && data.length > 0) {
        await fetchInvestments();
      }
      handleReset();
      handleCloseModal();
    } catch (error) {
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Update transaction
  const handleUpdateTransaction = async () => {
    if (!editMonthly || !editInterestRate || !editTenureMonths || !editStartDate) {
      alert('Please fill all required fields');
      return;
    }
    if (!currentTransaction || !currentTransaction.id) {
      alert('Cannot update transaction: Missing ID');
      return;
    }
    try {
      setLoading(true);
      const updatedTransaction = {
        name: currentTransaction.name,
        start_date: editStartDate instanceof Date 
          ? editStartDate.toISOString().split('T')[0] 
          : new Date(editStartDate).toISOString().split('T')[0],
        monthly_investment: parseFloat(editMonthly),
        interest_rate: parseFloat(editInterestRate),
        tenure_months: parseInt(editTenureMonths),
        notes: editNotes || null
      };
      const { data, error } = await supabase
        .from('recurring_deposit_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();
      if (error) throw error;
      await fetchInvestments();
      handleEditModalClose();
    } catch (error) {
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete transaction
  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('recurring_deposit_investments')
        .delete()
        .eq('id', currentTransaction.id);
      if (error) throw error;
      await fetchInvestments();
      handleDeleteDialogClose();
      // Refresh the current transaction list in the modal if it's open
      if (selectedAccount && openHistoryModal) {
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('recurring_deposit_investments')
          .select('*')
          .eq('name', selectedAccount.name)
          .order('start_date', { ascending: false });
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            setAccountTransactions(refreshedTransactions);
          } else {
            // No transactions left for this account, close the modal
            handleCloseHistoryModal();
          }
        }
      }
    } catch (error) {
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'monthly') {
        return direction === 'asc' 
          ? a.monthly - b.monthly
          : b.monthly - a.monthly;
      } else if (key === 'interestRate') {
        return direction === 'asc' 
          ? a.interestRate - b.interestRate
          : b.interestRate - a.interestRate;
      } else if (key === 'tenureMonths') {
        return direction === 'asc' 
          ? a.tenureMonths - b.tenureMonths
          : b.tenureMonths - a.tenureMonths;
      } else if (key === 'maturityDate') {
        const dateA = new Date(getMaturityDate(a.startDate, a.tenureMonths));
        const dateB = new Date(getMaturityDate(b.startDate, b.tenureMonths));
        return direction === 'asc' 
          ? dateA - dateB
          : dateB - dateA;
      } else if (key === 'contribution') {
        const contribA = calculateContribution(a.startDate, a.monthly);
        const contribB = calculateContribution(b.startDate, b.monthly);
        return direction === 'asc' 
          ? contribA - contribB
          : contribB - contribA;
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      }
      return 0;
    });
    setInvestments(sortedData);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/debt')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Recurring Deposits
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Current Value: {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 0,
              }).format(investments.reduce((sum, inv) => 
                sum + calculateContribution(inv.startDate, inv.monthly), 0
              ))}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchInvestments} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>
      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Recurring Deposit
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box component="form" noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Account Name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                  fullWidth
                  placeholder="Enter account name"
                  helperText="Account name must be unique"
                />
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, required: true }
                  }}
                />
                <AmountField
                  label="Monthly Investment"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(e.target.value)}
                  required
                  fullWidth
                  type="number"
                  inputProps={{ step: "1", min: "0" }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={numberInputStyle}
                />
                <TextField
                  label="Interest Rate (%)"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  required
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={numberInputStyle}
                />
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                  <TextField
                    label="Tenure (Years)"
                    value={tenureYears}
                    onChange={(e) => setTenureYears(e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ step: '1', min: '0' }}
                    helperText="Years"
                    sx={numberInputStyle}
                  />
                  <TextField
                    label="Tenure (Months)"
                    value={tenureMonths}
                    onChange={(e) => setTenureMonths(e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ step: '1', min: '0', max: '11' }}
                    helperText="0-11"
                    sx={numberInputStyle}
                  />
                </Box>
                <TextField
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="Additional details about this investment (optional)"
                />
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                  <Button 
                    variant="outlined" 
                    onClick={handleReset}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Reset
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSave}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Save Investment
                  </Button>
                </Box>
              </Stack>
            </Box>
          </LocalizationProvider>
        </Box>
      </Modal>
      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>
      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Account: {currentTransaction?.name}
              </Typography>
              <DatePicker
                label="Start Date"
                value={editStartDate}
                onChange={(newValue) => setEditStartDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              <AmountField
                label="Monthly Investment"
                value={editMonthly}
                onChange={(e) => setEditMonthly(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              <TextField
                label="Interest Rate (%)"
                value={editInterestRate}
                onChange={(e) => setEditInterestRate(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              <TextField
                label="Tenure (Months)"
                value={editTenureMonths}
                onChange={(e) => setEditTenureMonths(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "1" }}
                sx={numberInputStyle}
              />
              <TextField
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Account: {currentTransaction.name}<br />
                Start Date: {currentTransaction.startDate ? new Date(currentTransaction.startDate).toLocaleDateString('en-IN') : '-'}<br />
                Monthly: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.monthly)}<br />
                Tenure: {currentTransaction.tenureMonths} months
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Recurring Deposits Found</Typography>
          <Typography variant="body2">
            No recurring deposit investments found. Start investing to see them here.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          userSelect: 'none'
                        }}
                      >
                        Account Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('monthly')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Instalment
                        {sortConfig.key === 'monthly' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('interestRate')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Interest Rate
                        {sortConfig.key === 'interestRate' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        data-tour="inv-rd-instalments"
                        align="right"
                        onClick={() => handleSort('tenureMonths')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Tenure (Months)
                        {sortConfig.key === 'tenureMonths' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('maturityDate')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Maturity Date
                        {sortConfig.key === 'maturityDate' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('contribution')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Contribution
                        {sortConfig.key === 'contribution' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        data-tour="inv-rd-maturity"
                        align="right"
                        onClick={() => handleSort('value')}
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                      >
                        Maturity Value
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => handleOpenTransactionHistory(investment)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2,
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {typeof investment.monthly === 'number' && !isNaN(investment.monthly)
                            ? new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                              }).format(investment.monthly)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {investment.interestRate ? `${investment.interestRate}%` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const progress = calculateInstallmentProgress(
                              investment.startDate,
                              investment.tenureMonths
                            );
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                <Typography variant="body2">
                                  {`${progress.paid}/${progress.total}`}
                                </Typography>
                                <Box sx={{ width: 60 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(progress.paid / progress.total) * 100}
                                    sx={{ height: 6, borderRadius: 1 }}
                                  />
                                </Box>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {getMaturityDate(investment.startDate, investment.tenureMonths)}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(calculateContribution(investment.startDate, investment.monthly))}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View transactions">
                            <IconButton size="small" sx={{ color: 'primary.main' }}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(investments.reduce((sum, inv) => 
                          sum + calculateContribution(inv.startDate, inv.monthly), 0
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Paper
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={450}
                    legend={{
                      hidden: true
                    }}
                    margin={{ top: 10, bottom: 50, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'middle' },
                        padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 15,
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 2,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      {/* Transaction History Modal */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedAccount?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maturity Value: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedAccount?.value || 0)}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseHistoryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Start Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Monthly (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Interest Rate (%)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Tenure (Months)</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Notes</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accountTransactions.length > 0 ? (
                  accountTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.startDate 
                          ? new Date(transaction.startDate).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.monthly)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.interestRate 
                          ? parseFloat(transaction.interestRate).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.tenureMonths}
                      </TableCell>
                      <TableCell>
                        {transaction.notes || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {accountTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(accountTransactions.reduce((sum, tx) => sum + tx.monthly, 0))}
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseHistoryModal}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <GuidedTour steps={TOURS['inv-debt-recurring-deposit']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default RecurringDeposit; 